<script setup lang="ts">
import { BaseDialog } from '@/components/Modals/Dialog'
import store from '@/store'
import { EShuttleEpcActions } from '@/store/shuttleEpcStore/ShuttleEpcStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PrimaryButton from '../../../../components/Buttons/PrimaryButton.vue'
import CustomField from '../../../../components/Fields/CustomField.vue'
import InputField from '../../../../components/Fields/InputField.vue'
import StackLayout from '../../../../components/Layouts/StackLayout.vue'
import TypographyItem from '../../../../components/Typography/TypographyItem.vue'
import { EColors } from '../../../../types/constants/ColorValues.js'

const { t } = useI18n()

type Props = {
    loading?: boolean
}
defineProps<Props>()
type Emits = {
    (e: 'refresh'): void
}
const emit = defineEmits<Emits>()

const data = computed(() => store.state.shuttleEpc)
// Must match StoreShuttleEpcRangeRequest::MAX_RANGE_SIZE on the API
const MAX_RANGE_SIZE = 1000

const isCreateRangeDialogOpen = ref(false)
const rangeStart = ref('')
const rangeEnd = ref('')

// EPCs can exceed Number.MAX_SAFE_INTEGER, hence BigInt
const rangeValidation = computed<{ count: number } | { error: string } | null>(() => {
    const start = rangeStart.value.trim()
    const end = rangeEnd.value.trim()
    if (!start || !end) return null
    if (!/^\d+$/.test(start) || !/^\d+$/.test(end)) {
        return { error: t('shuttleEpc.dialog.rangeErrors.digits') }
    }
    if (start.length !== end.length) return { error: t('shuttleEpc.dialog.rangeErrors.length') }
    const count = BigInt(end) - BigInt(start) + 1n
    if (count < 1n) return { error: t('shuttleEpc.dialog.rangeErrors.order') }
    if (count > BigInt(MAX_RANGE_SIZE)) {
        return { error: t('shuttleEpc.dialog.rangeErrors.max', { max: MAX_RANGE_SIZE }) }
    }
    return { count: Number(count) }
})
const isRangeValid = computed(() => !!rangeValidation.value && 'count' in rangeValidation.value)

const openCreateRangeDialog = () => {
    rangeStart.value = ''
    rangeEnd.value = ''
    isCreateRangeDialogOpen.value = true
}

const createShuttleEpcRange = () => {
    if (!isRangeValid.value) return
    store
        .dispatch(`${EStoreModules.SHUTTLE_EPC}/${EShuttleEpcActions.CREATE_SHUTTLE_EPC_RANGE}`, {
            start: rangeStart.value.trim(),
            end: rangeEnd.value.trim()
        })
        .then(() => {
            isCreateRangeDialogOpen.value = false
            emit('refresh')
        })
}
</script>

<template>
    <PrimaryButton
        icon-left="list-check"
        :label="$t('shuttleEpc.buttons.createRange')"
        @click="openCreateRangeDialog" />

    <BaseDialog
        :title="t('shuttleEpc.dialog.createRangeTitle')"
        :open="isCreateRangeDialogOpen"
        :confirmLabel="t('common.create')"
        :disabled="!isRangeValid"
        :loading="loading"
        @close="isCreateRangeDialogOpen = false"
        @confirm="createShuttleEpcRange">
        <template #content>
            <StackLayout direction="column" :gap="8">
                <TypographyItem :label="$t('shuttleEpc.dialog.rangeHint')" />
                <CustomField :label="t('shuttleEpc.dialog.rangeStart')">
                    <InputField v-model="rangeStart" placeholder="00001" inputmode="numeric" />
                </CustomField>
                <CustomField
                    :label="t('shuttleEpc.dialog.rangeEnd')"
                    :type="rangeValidation && 'error' ? EColors.DANGER : undefined"
                    :message="
                        rangeValidation && 'error' in rangeValidation
                            ? rangeValidation.error
                            : rangeValidation
                            ? t('shuttleEpc.dialog.rangeCount', { count: rangeValidation.count })
                            : ''
                    ">
                    <InputField
                        v-model="rangeEnd"
                        placeholder="60200"
                        inputmode="numeric"
                        @keyup.enter="createShuttleEpcRange" />
                </CustomField>
            </StackLayout>
        </template>
    </BaseDialog>
</template>
